import {
  createMembershipGrid,
  createSvgEl,
  type MembershipGridHandle,
} from '@learnforge/demo-render';
import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import type { SetsContext, SetsKey } from '../types';
import type { Pair } from '../sets';
import { partitionToEquivalence, equivalenceToPartition, bellNumber, quotient } from '../sets';

// ── State ─────────────────────────────────────────────────────────────────────

export interface State {
  elements: string[];
  blocks: string[][];
  mode: 'build-partition' | 'toggle-relation';
  pairs: Pair[];
  advanced: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_ELEMENTS = ['a', 'b', 'c', 'd', 'e'];

/** Initial partition: two blocks {a,b} and {c,d,e}. */
const DEFAULT_BLOCKS: string[][] = [
  ['a', 'b'],
  ['c', 'd', 'e'],
];

// ── Pure helpers ──────────────────────────────────────────────────────────────

/**
 * Cycle an element to the next block (wraps to new solo block then back).
 *
 * Rules:
 *  - If the element is alone in its block, move it to the first block that
 *    has more than one element. If all blocks are singletons, merge into [0].
 *  - Otherwise, remove it from its current block and place it into the NEXT
 *    block (by index), creating a new singleton block after the last block
 *    when the element is already in the last block.
 *
 * After the move, empty blocks are removed.
 *
 * This is a pure function useful for click-to-cycle element placement.
 */
export function cycleElementBlock(blocks: string[][], element: string): string[][] {
  const currentBlockIndex = blocks.findIndex((b) => b.includes(element));
  if (currentBlockIndex === -1) {
    // Element not found — return unchanged
    return blocks;
  }

  // Remove element from its current block
  const next: string[][] = blocks.map((b) => b.filter((e) => e !== element));

  const nextBlockIndex = (currentBlockIndex + 1) % (blocks.length + 1);

  if (nextBlockIndex < next.length) {
    // Insert into the existing next block
    next[nextBlockIndex] = [...(next[nextBlockIndex] ?? []), element];
  } else {
    // Create a new block (singleton)
    next.push([element]);
  }

  // Remove any empty blocks
  return next.filter((b) => b.length > 0);
}

/** Toggle a pair in the pairs list — add if absent, remove if present. */
export function toggleRelationPair(pairs: Pair[], from: string, to: string): Pair[] {
  const exists = pairs.some(([a, b]) => a === from && b === to);
  if (exists) {
    return pairs.filter(([a, b]) => !(a === from && b === to));
  }
  return [...pairs, [from, to] as Pair];
}

// ── SVG layout constants ───────────────────────────────────────────────────────

const NODE_R = 18;
const BLOCK_PAD = 16;
const BLOCK_GAP = 24;
const SVG_HEIGHT = 260;

/**
 * Lay out blocks as horizontal clusters in an SVG.
 * Returns the computed SVG width and position data per element.
 */
function computeLayout(
  elements: string[],
  blocks: string[][],
): {
  svgWidth: number;
  elementPositions: Map<string, { x: number; y: number; blockIndex: number }>;
  blockRects: { x: number; y: number; w: number; h: number }[];
} {
  const elementPositions = new Map<string, { x: number; y: number; blockIndex: number }>();
  const blockRects: { x: number; y: number; w: number; h: number }[] = [];

  // Find any elements not in any block (orphans) — place them in a virtual block at the end
  const allInBlocks = new Set(blocks.flat());
  const orphans = elements.filter((e) => !allInBlocks.has(e));
  const allBlocks = orphans.length > 0 ? [...blocks, orphans] : blocks;

  let cursorX = BLOCK_PAD;
  const centerY = SVG_HEIGHT / 2;

  for (let bi = 0; bi < allBlocks.length; bi++) {
    const block = allBlocks[bi] ?? [];
    const count = block.length;

    // Arrange nodes in a column
    const nodeSpacing = NODE_R * 2 + 8;
    const blockH = count * nodeSpacing + BLOCK_PAD * 2;
    const blockW = NODE_R * 2 + BLOCK_PAD * 2;

    const blockX = cursorX;
    const blockY = centerY - blockH / 2;

    blockRects.push({ x: blockX, y: blockY, w: blockW, h: blockH });

    for (let ni = 0; ni < count; ni++) {
      const elem = block[ni] ?? '';
      const nx = blockX + blockW / 2;
      const ny = blockY + BLOCK_PAD + NODE_R + ni * nodeSpacing;
      elementPositions.set(elem, { x: nx, y: ny, blockIndex: bi });
    }

    cursorX += blockW + BLOCK_GAP;
  }

  return { svgWidth: cursorX, elementPositions, blockRects };
}

// ── Spec ──────────────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.equivalence-partition.label',
    demoId: 'sets',
    advancedKey: 'equivalence-partition',

    initialState(ctx): State {
      return {
        elements: DEFAULT_ELEMENTS.slice(),
        blocks: DEFAULT_BLOCKS.map((b) => b.slice()),
        mode: ctx.advanced ? 'toggle-relation' : 'build-partition',
        pairs: [],
        advanced: ctx.advanced,
      };
    },

    panel(state, ctx): PanelRow<SetsKey, State>[] {
      const advanced = ctx.advanced;
      const rows: PanelRow<SetsKey, State>[] = [
        {
          kind: 'advanced-toggle',
          labelKey: 'btn.advanced',
        },
      ];

      if (advanced) {
        rows.splice(0, 0, {
          kind: 'dropdown',
          id: 'ep-mode',
          labelKey: 'param.ep-mode',
          options: [
            { value: 'build-partition', labelKey: 'mode.build-partition' as SetsKey },
            { value: 'toggle-relation', labelKey: 'mode.toggle-relation' as SetsKey },
          ],
          value: state.mode,
          onChange: (v) => ({
            ...state,
            mode: v as State['mode'],
          }),
        });
      }

      return rows;
    },

    createRender(ctx, host, controls) {
      const container = document.createElement('div');
      container.className = 'ep-container';
      host.appendChild(container);

      // ── Left: SVG cluster / relation grid ──
      const leftCol = document.createElement('div');
      leftCol.className = 'ep-left-col';
      container.appendChild(leftCol);

      // SVG for build-partition mode
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'ep-svg');
      svg.setAttribute('height', String(SVG_HEIGHT));
      leftCol.appendChild(svg);

      // Wrapper for toggle-relation grid
      const gridWrapper = document.createElement('div');
      gridWrapper.className = 'ep-grid-wrapper';
      leftCol.appendChild(gridWrapper);

      // ── Right: readouts ──
      const rightCol = document.createElement('div');
      rightCol.className = 'ep-right-col';
      container.appendChild(rightCol);

      const partitionReadout = document.createElement('div');
      partitionReadout.className = 'ep-readout';
      rightCol.appendChild(partitionReadout);

      const bellReadout = document.createElement('div');
      bellReadout.className = 'ep-readout ep-readout--bell';
      rightCol.appendChild(bellReadout);

      const quotientReadout = document.createElement('div');
      quotientReadout.className = 'ep-readout ep-readout--quotient';
      rightCol.appendChild(quotientReadout);

      const errorReadout = document.createElement('div');
      errorReadout.className = 'ep-error';
      rightCol.appendChild(errorReadout);

      // Induced equivalence readout (build-partition)
      const inducedReadout = document.createElement('div');
      inducedReadout.className = 'ep-readout ep-readout--induced';
      rightCol.appendChild(inducedReadout);

      let gridHandle: MembershipGridHandle | null = null;
      // Track click listeners for cleanup
      const nodeListeners = new Map<Element, () => void>();

      function clearNodeListeners(): void {
        for (const [el, fn] of nodeListeners) {
          el.removeEventListener('click', fn);
        }
        nodeListeners.clear();
      }

      function renderBuildPartition(state: State): void {
        svg.style.display = '';
        gridWrapper.style.display = 'none';

        // Clear previous SVG content
        svg.innerHTML = '';

        const { elements, blocks } = state;
        const { svgWidth, elementPositions, blockRects } = computeLayout(elements, blocks);
        svg.setAttribute('width', String(svgWidth));
        svg.setAttribute('viewBox', `0 0 ${svgWidth} ${SVG_HEIGHT}`);

        // Draw block blobs (rounded rectangles)
        for (let bi = 0; bi < blockRects.length; bi++) {
          const rect = blockRects[bi];
          if (!rect) continue;
          const blobG = createSvgEl('g', { class: 'ep-block-blob' });
          const blobRect = createSvgEl('rect', {
            class: 'ep-block-rect',
            x: rect.x,
            y: rect.y,
            width: rect.w,
            height: rect.h,
            rx: NODE_R + 4,
            ry: NODE_R + 4,
          });
          blobG.appendChild(blobRect);

          // Block label: "B₁", "B₂", etc.
          const subscriptDigits = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
          const sub = subscriptDigits[bi] ?? String(bi);
          const blobLabel = createSvgEl('text', {
            class: 'ep-block-label',
            x: rect.x + rect.w / 2,
            y: rect.y + 14,
            'text-anchor': 'middle',
          });
          blobLabel.textContent = `B${sub}`;
          blobG.appendChild(blobLabel);

          svg.appendChild(blobG);
        }

        // Draw within-block equivalence links (subtle lines between nodes in same block)
        const pairsFromBlocks = partitionToEquivalence(blocks);
        for (const [from, to] of pairsFromBlocks) {
          if (from === to) continue; // skip self-loops for cleanliness
          const posA = elementPositions.get(from);
          const posB = elementPositions.get(to);
          if (!posA || !posB) continue;
          // Only draw upward links to avoid duplicates
          if (posA.y >= posB.y) continue;
          const line = createSvgEl('line', {
            class: 'ep-equiv-link',
            x1: posA.x,
            y1: posA.y,
            x2: posB.x,
            y2: posB.y,
          });
          svg.appendChild(line);
        }

        // Draw element nodes (clickable circles)
        clearNodeListeners();
        for (const [elem, pos] of elementPositions) {
          const nodeG = createSvgEl('g', {
            class: 'ep-node',
            transform: `translate(${pos.x},${pos.y})`,
          });

          const circle = createSvgEl('circle', {
            class: 'ep-node-circle',
            cx: 0,
            cy: 0,
            r: NODE_R,
          });
          nodeG.appendChild(circle);

          const label = createSvgEl('text', {
            class: 'ep-node-label',
            x: 0,
            y: 0,
            'text-anchor': 'middle',
            'dominant-baseline': 'central',
          });
          label.textContent = elem;
          nodeG.appendChild(label);

          svg.appendChild(nodeG);

          // Click handler: cycle this element to the next block
          const clickHandler = (): void => {
            controls.setState((prev) => ({
              ...prev,
              blocks: cycleElementBlock(prev.blocks, elem),
            }));
          };
          nodeG.addEventListener('click', clickHandler);
          nodeListeners.set(nodeG, clickHandler);
        }

        // Partition readout
        const blockStrs = blocks.map((b) => `{${b.join(', ')}}`).join(', ');
        partitionReadout.textContent = `${ctx.i18n.t('stat.block-count')}: ${blocks.length} — {${blockStrs.slice(1, -1)}}`;
        bellReadout.textContent = `B(${elements.length}) = ${bellNumber(elements.length)}`;
        quotientReadout.textContent = '';
        errorReadout.textContent = '';

        // Induced equivalence: show count of pairs
        const inducedPairs = partitionToEquivalence(blocks);
        inducedReadout.textContent = `∼ = ${inducedPairs.length} pairs`;
      }

      function renderToggleRelation(state: State): void {
        svg.style.display = 'none';
        gridWrapper.style.display = '';

        clearNodeListeners();

        const { elements, pairs } = state;
        const pairSet = new Set(pairs.map(([a, b]) => `${a}:${b}`));

        // Determine witness pairs for highlighting
        const result = equivalenceToPartition(pairs, elements);
        let witnessSet: Set<string> = new Set();
        if (!result.ok && result.witness) {
          witnessSet = new Set(result.witness.map(([a, b]) => `${a}:${b}`));
        }

        // Build clickable pair grid using membership-grid
        const cols = elements.map((e) => ({ id: e, label: e }));

        const gridOpts = {
          universe: elements,
          columns: cols,
          isMember(i: number, colId: string): boolean {
            const elem = elements[i] ?? '';
            return pairSet.has(`${elem}:${colId}`);
          },
          highlightColumn: null as string | null,
        };

        if (gridHandle === null) {
          gridHandle = createMembershipGrid(gridWrapper, gridOpts);
          // Add click listener for pair toggling
          const table = gridHandle.element;
          table.addEventListener('click', (e: Event) => {
            const td = (e.target as Element).closest('td');
            if (!td) return;
            const tr = td.closest('tr');
            if (!tr) return;
            const tbody = tr.closest('tbody');
            if (!tbody) return;
            const rowIndex = Array.from(tbody.rows).indexOf(tr);
            if (rowIndex < 0) return;
            // Get column index from th data
            const table2 = tbody.closest('table');
            if (!table2) return;
            const ths = Array.from(table2.querySelectorAll('thead th'));
            const colIndex = Array.from(tr.cells).indexOf(td);
            if (colIndex <= 0) return; // 0 is the element label column
            const colId =
              ths[colIndex]?.getAttribute('data-col-id') ?? ths[colIndex]?.textContent ?? '';
            const fromElem = elements[rowIndex] ?? '';
            if (!fromElem || !colId) return;
            controls.setState((prev) => ({
              ...prev,
              pairs: toggleRelationPair(prev.pairs, fromElem, colId),
            }));
          });
        } else {
          gridHandle.update(gridOpts);
        }

        // Highlight witness cells
        const table = gridHandle.element;
        const tds = table.querySelectorAll('tbody td');
        tds.forEach((td) => {
          td.classList.remove('ep-witness');
        });
        if (witnessSet.size > 0) {
          const rows = table.querySelectorAll('tbody tr');
          rows.forEach((tr, ri) => {
            const from = elements[ri] ?? '';
            const cells = tr.querySelectorAll('td');
            cells.forEach((td, ci) => {
              if (ci === 0) return;
              const to = elements[ci - 1] ?? '';
              if (witnessSet.has(`${from}:${to}`)) {
                td.classList.add('ep-witness');
              }
            });
          });
        }

        // Readouts
        if (result.ok && result.blocks) {
          const blocks = result.blocks;
          const blockStrs = blocks.map((b) => `{${b.join(', ')}}`).join(', ');
          partitionReadout.textContent = `${ctx.i18n.t('stat.block-count')}: ${blocks.length} — {${blockStrs.slice(1, -1)}}`;
          bellReadout.textContent = `B(${elements.length}) = ${bellNumber(elements.length)}`;

          const qBlocks = quotient(elements, pairs);
          const quotientStr = qBlocks.map((b) => `[${b[0] ?? ''}]`).join(', ');
          quotientReadout.textContent = `${ctx.i18n.t('stat.quotient')}: A/∼ = {${quotientStr}}`;
          errorReadout.textContent = '';
          errorReadout.className = 'ep-error';
          inducedReadout.textContent = '';
        } else if (!result.ok) {
          partitionReadout.textContent = '';
          bellReadout.textContent = '';
          quotientReadout.textContent = '';
          inducedReadout.textContent = '';

          let msgKey: SetsKey = 'message.not-reflexive';
          if (result.reason === 'symmetric') msgKey = 'message.not-symmetric';
          else if (result.reason === 'transitive') msgKey = 'message.not-transitive';

          errorReadout.textContent = ctx.i18n.t(msgKey);
          errorReadout.className = 'ep-error ep-error--active';
        } else {
          partitionReadout.textContent = '';
          bellReadout.textContent = '';
          quotientReadout.textContent = '';
          inducedReadout.textContent = '';
          errorReadout.textContent = '';
          errorReadout.className = 'ep-error';
        }
      }

      function update(state: State): void {
        if (state.mode === 'build-partition') {
          // Destroy grid handle if switching from toggle-relation
          if (gridHandle !== null) {
            gridHandle.destroy();
            gridHandle = null;
          }
          renderBuildPartition(state);
        } else {
          renderToggleRelation(state);
        }
      }

      controls.registerCleanup(() => {
        clearNodeListeners();
        gridHandle?.destroy();
        gridHandle = null;
      });

      return {
        update,
        dispose() {
          clearNodeListeners();
          gridHandle?.destroy();
          gridHandle = null;
          if (container.parentNode) {
            container.parentNode.removeChild(container);
          }
        },
      };
    },

    actions(state, ctx) {
      return {
        onReset() {
          return {
            elements: DEFAULT_ELEMENTS.slice(),
            blocks: DEFAULT_BLOCKS.map((b) => b.slice()),
            mode: ctx.advanced ? 'toggle-relation' : 'build-partition',
            pairs: [],
            advanced: ctx.advanced,
          };
        },
        onRandomise() {
          const elements = state.elements;
          const n = elements.length;
          // Build a random partition using ctx.prng
          const assignment = elements.map(() => Math.floor(ctx.prng() * 3));
          const blockMap = new Map<number, string[]>();
          for (let i = 0; i < elements.length; i++) {
            const k = assignment[i] ?? 0;
            if (!blockMap.has(k)) blockMap.set(k, []);
            blockMap.get(k)!.push(elements[i] ?? '');
          }
          const newBlocks = Array.from(blockMap.values()).filter((b) => b.length > 0);
          // Ensure all elements are covered (in case prng gives same number for all)
          const covered = new Set(newBlocks.flat());
          const uncovered = elements.filter((e) => !covered.has(e));
          if (uncovered.length > 0) newBlocks.push(uncovered);

          if (state.mode === 'toggle-relation') {
            // Random pairs
            const newPairs: Pair[] = [];
            for (const a of elements) {
              for (const b of elements) {
                if (ctx.prng() < 0.35) newPairs.push([a, b] as Pair);
              }
            }
            return { ...state, pairs: newPairs };
          }
          return { ...state, blocks: newBlocks, n };
        },
        presets: [
          { id: 'two-blocks', labelKey: 'preset.two-blocks' as SetsKey },
          { id: 'singletons', labelKey: 'preset.singletons' as SetsKey },
          { id: 'one-block', labelKey: 'preset.one-block' as SetsKey },
        ],
        onPreset(s: State, id: string) {
          switch (id) {
            case 'two-blocks':
              return {
                ...s,
                blocks: [
                  ['a', 'b'],
                  ['c', 'd', 'e'],
                ],
                mode: 'build-partition' as const,
                pairs: [],
              };
            case 'singletons':
              return {
                ...s,
                blocks: s.elements.map((e) => [e]),
                mode: 'build-partition' as const,
                pairs: [],
              };
            case 'one-block':
              return {
                ...s,
                blocks: [s.elements.slice()],
                mode: 'build-partition' as const,
                pairs: [],
              };
            default:
              return s;
          }
        },
      };
    },

    applyShotState(state): State {
      // 3-block partition: {a,b}, {c,d}, {e}
      return {
        ...state,
        elements: DEFAULT_ELEMENTS.slice(),
        blocks: [['a', 'b'], ['c', 'd'], ['e']],
        mode: 'build-partition',
        pairs: [],
        advanced: false,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
